// Public wire formats only. Never include PRF results, wrapping keys, or seeds.
import { z } from "zod";

export const base64url = z.string().regex(/^[A-Za-z0-9_-]+$/);
export const credentialIdSchema = base64url.min(1).max(1400);
const legacyKeyBackupSchema = z.object({
  version: z.literal(1),
  salt: base64url.length(43), // HKDF salt (32 bytes), not a secret
  iv: base64url.length(16), // AES-GCM nonce (12 bytes)
  ciphertext: base64url.length(107), // 64-byte seed + 16-byte GCM tag
}).strict();
// v2 retains BIP-39 entropy inside authenticated ciphertext, so an emergency
// phrase can be recovered on-device. v1 seed-only backups remain readable.
export const keyBackupSchema = z.discriminatedUnion("version", [legacyKeyBackupSchema, z.object({
  version: z.literal(2), salt: base64url.length(43), iv: base64url.length(16),
  ciphertext: base64url.length(43), // 16-byte entropy + 16-byte GCM tag
}).strict()]);
export type KeyBackup = z.infer<typeof keyBackupSchema>;
export interface BackupBinding {
  rpId: string;
  accountXid: string;
  identityPublicKey: string;
  credentialId: string;
}

export function backupBindingBytes(binding: BackupBinding, version: 1 | 2 = 1): Uint8Array {
  return new TextEncoder().encode(JSON.stringify([
    "link.identity-backup", version, binding.rpId, binding.accountXid,
    binding.identityPublicKey, binding.credentialId,
  ]));
}

// Credential extensions are deliberately absent: PRF outputs never cross HTTP.
export const publicAssertionSchema = z.object({
  id: credentialIdSchema, rawId: credentialIdSchema, type: z.literal("public-key"),
  response: z.object({ clientDataJSON: z.string().max(8192), authenticatorData: z.string().max(8192), signature: z.string().max(2048), userHandle: z.string().max(256).optional() }).strict(),
  clientExtensionResults: z.object({}).strict(),
}).strict();
export const publicRegistrationSchema = z.object({
  id: credentialIdSchema, rawId: credentialIdSchema, type: z.literal("public-key"),
  response: z.object({ clientDataJSON: z.string().max(8192), attestationObject: z.string().max(20000) }).strict(),
  clientExtensionResults: z.object({}).strict(),
}).strict();
export const signupPasskeySchema = z.object({
  flowId: base64url.length(43), registration: publicRegistrationSchema,
  assertion: publicAssertionSchema, backup: keyBackupSchema,
  proof: base64url.length(86),
}).strict();
export type SignupPasskey = z.infer<typeof signupPasskeySchema>;

export function enrollmentProofBytes(flowId: string, binding: BackupBinding, backup: KeyBackup): Uint8Array {
  return new TextEncoder().encode(JSON.stringify([
    "link.passkey.enroll", 1, flowId, binding.rpId, binding.accountXid,
    binding.identityPublicKey, binding.credentialId,
    backup.version, backup.salt, backup.iv, backup.ciphertext,
  ]));
}

export function removalProofBytes(flowId: string, accountXid: string, credentialId: string): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(["link.passkey.remove", 1, flowId, accountXid, credentialId]));
}

export function toBase64url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromBase64url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("Invalid base64url");
  const bytes = Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4)), c => c.charCodeAt(0));
  if (toBase64url(bytes) !== value) throw new Error("Non-canonical base64url");
  return bytes;
}
